import { cloneDeep } from "lodash";
import moment from "moment";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Option } from "../../../common/types/Option";
import { reportPlanCashFilterOptions, reportPlanCashIndicators, allPlanCashClassificators } from "../report-plan-cash";
import { ReportPlanCashTableDataRequest } from "../ReportPlanCashTableData";
import { ReportPlanCashJournalRequest } from "../journal/ReportPlanCashJournal";
import { ReportPlanCashFilterSettingsForm } from "../settings-form/ReportPlanCashFilterSettingsForm";
import { ReportPlanCashFilterSettings } from "../settings/ReportPlanCashFilterSettings";
import { BudgetType, MutuallyRedeemingLevel } from "../../../common/types/roots/fields";
import { ReportPlanCashResponse } from "../ReportPlanCashResponse";
import { ReportPlanCashTableData } from "../table-data/table-data-types";

export class ReportPlanCashFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportPlanCashFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'income_kbk') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'income_kbk') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({...option})
        })

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption } 

        const regions: Option<string>[] = []
        settings.regions.forEach(region => {
            const option = options.regions.find(it => it.value === region)!
            regions.push({...option})
        })

        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        const statuses = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const budgetTypes: Option<BudgetType>[] = []
        settings.budgetTypes.forEach(budget => { // TODO: just filter
            const budgetTypeOption = options.budgetTypes.find(item => item.value === budget)!
            budgetTypes.push({...budgetTypeOption})
        })

        // 2. Формирование
        const form: ReportPlanCashFilterSettingsForm = {
            // Base:
            key: 'income_kbk',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            mutuallyRedeeming: mutuallyRedeemingOption,
            measureUnit: measureUnitOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null,
            // Custom:
            regions: regions,
            date: new Date(settings.date),
            budgetTypes: budgetTypes,
            statuses: statuses,
            indicators: cloneDeep(settings.indicators),
            incomes: cloneDeep(settings.incomes),
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            sortByRegionCode: settings.sortByRegionCode ?? false,
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'income_kbk') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.regions.length === 0) {
            throw new Error('error_select_region')
        }
        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }
        
        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: ReportPlanCashFilterSettings = {
            // Base:
            reportName: 'income_kbk',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            budgetTypes: form.budgetTypes.map(it => it.value),
            // Custom:
            regions: form.regions.map(it => it.value),
            date: strDate,
            indicators: cloneDeep(form.indicators),
            statuses: form.statuses.map(s => s.value).includes('0') ? ['26', '39'] : form.statuses.map(s => s.value),
            incomes: cloneDeep(form.incomes),
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            sortByRegionCode: form.sortByRegionCode ?? false,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportPlanCashFilterOptions)

        const emptyForm: ReportPlanCashFilterSettingsForm = {
            key: 'income_kbk',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'income_kbk')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            roundUp: false,
            roundUpTo: null,
            budgetTypes: [],
            // Custom fields
            regions: [],
            date: null,
            statuses: [options.statuses.find(it => it.value === '39')!],
            indicators: cloneDeep(reportPlanCashIndicators),
            incomes: cloneDeep(allPlanCashClassificators).map(it =>
                it.key === 'kat' ? { ...it, selected: [] } : it
            ),
            mutuallyRedeemingLevels: [],
            sortByRegionCode: false,
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'income_kbk') {
            throw new Error('error_type_filter_settingsForm')
        }
        
        const date = form.date
        if (!date) {
            throw new Error('error_input_date')
        }
        
        if ('regions' in form && form.regions.length === 0) {
            throw new Error('error_input_region')
        }

        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        if ('regions' in form && form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        // Фиксированная разрезность: сначала КБК, потом регионы
        const cutness = ['KBK', 'REGION']

        const strDate = moment(date).format('YYYY-MM-DD')

        const tempIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        // "Все" (value '0') означает статусы '26' и '39'
        const rawStatuses = form.statuses.map(status => status.value)
        const resolvedStatuses = rawStatuses.includes('0') ? ['26', '39'] : rawStatuses

        const params: ReportPlanCashTableDataRequest = {
            incomes: tempIncomes,
            date: strDate,
            regions: form.regions.map(it => it.value),
            vzk: form.mutuallyRedeeming?.value,
            measureUnit: form.measureUnit.value,
            cutness: cutness,
            roundTo: roundUpTo,
            statuses: resolvedStatuses,
            budget_types: form.budgetTypes.map(it => it.value),
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => {
                // Конвертируем 'TYPE-3' -> 3, 'TYPE-4' -> 4
                const match = it.value.match(/TYPE-(\d+)/)
                return match ? parseInt(match[1]) : 0
            }),
            dataSource: form.dataSource.value,
            sort_by_region_code: form.sortByRegionCode ?? false,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/plan-cash',
            {
                method: 'POST',
                cache: 'no-cache',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseData: ReportPlanCashResponse = await response.json()
        const tableData: ReportPlanCashTableData = responseData

        if ('template3_items' in tableData){
            tableData.template3_items?.forEach(r => {
                r.visible = true
                r.is_expanded = true
            })
        }
        if ('template4_items_structured_regions' in tableData){
            tableData.template4_items_structured_regions?.forEach(r => {
                if ('childs' in r) {            
                    r.is_childs_visible = true
                }
            })
        }

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportPlanCashJournalRequest = {
            reportName: "income_kbk",

            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // indicators_order: form.indicators.filter(it => it.active).map(it => it.key),

            classificators: tempIncomes,
            regions: form.regions.map(it => it.value),
            report_date: strDate,
            measure_unit: form.measureUnit.value,
            actually_regional: tableData.actually_regional ?? null,
            template1_items: tableData.template1_items ?? null,
            template1_items_total: tableData.template1_items_total ?? null,
            template1_regional_budget_decrypt_items: tableData.template1_regional_budget_decrypt_items ?? null,
            template2_items: tableData.template2_items ?? null,
            template1_items_districts_total: tableData.template1_items_districts_total ?? null,
            template1_items_cities_total: tableData.template1_items_cities_total ?? null,
            template2_items_total: tableData.template2_items_total ?? null,
            template2_items_tree: tableData.template2_items_tree ?? null,
            template3_items: tableData.template3_items ?? null,
            template4_items: tableData.template4_items ?? null,
            template4_items_structured_dspk: tableData.template4_items_structured_dspk?? null,
            template4_items_structured_regions: tableData.template4_items_structured_regions ?? null,
            plan_cash_monthly_data: tableData.plan_cash_monthly_data ?? null,
            decodingActualRegionalBudget: false,
            budget_types: form.budgetTypes.map(it => it.value),
            cutness: cutness,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
